import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ChatListItemComponent } from '../../components/chats-list/chats-list-item/chats-list-item.component';
import { ToolGroupComponent } from '../../components/tool-group/tool-group.component';
import { useRequest } from '../../hooks/useRequest';
import {
  AdminMcpTool,
  AdminRole,
  AdminTool,
  AdminUser,
  applyAdminUserToolPreset,
  assignAdminUserRole,
  connectAdminUserContentfulTag,
  connectAdminUserTool,
  ContentfulTag,
  deleteAdminUser,
  disconnectAdminUserContentfulTag,
  disconnectAdminUserTool,
  fetchAdminRoles,
  fetchAdminUser,
  fetchAdminUserMcpTools,
  fetchBuiltinTools,
  fetchContentfulTags,
  fetchToolPresets,
  regenerateAdminUserToken,
  setAdminUserContentfulPat,
  ToolPreset,
} from '../../utils/axios/api';
import { extractExceptionCode } from '../../utils/exception';

export function AdminUserPage() {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const loadUserRequest = useRequest<AdminUser>();
  const loadBuiltinToolsRequest = useRequest<AdminTool[]>();
  const loadToolsRequest = useRequest<AdminMcpTool[]>();
  const regenerateRequest = useRequest<{ token: string }>();
  const savePatRequest = useRequest<AdminUser>();
  const changeRoleRequest = useRequest<AdminUser>();
  const toggleToolRequest = useRequest<AdminUser>();
  const applyPresetRequest = useRequest<AdminUser>();
  const toggleTagRequest = useRequest<AdminUser>();
  const deleteRequest = useRequest();
  const [user, setUser] = useState<AdminUser | null>(null);
  const [builtinTools, setBuiltinTools] = useState<AdminTool[]>([]);
  const [tools, setTools] = useState<AdminMcpTool[]>([]);
  const [toolPresets, setToolPresets] = useState<ToolPreset[]>([]);
  const [contentfulTags, setContentfulTags] = useState<ContentfulTag[]>([]);
  const [roles, setRoles] = useState<AdminRole[]>([]);
  const [authToken, setAuthToken] = useState('');
  const [patToken, setPatToken] = useState('');
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [pendingToolName, setPendingToolName] = useState<string | null>(null);
  const [pendingPresetId, setPendingPresetId] = useState<string | null>(null);
  const [pendingTagId, setPendingTagId] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [rolesError, setRolesError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const loadBuiltinTools = useCallback(async () => {
    const data = await loadBuiltinToolsRequest.fetch(() => fetchBuiltinTools());
    setBuiltinTools(data ?? []);
  }, [loadBuiltinToolsRequest]);

  const loadTools = useCallback(
    async (userId: string) => {
      const mcpTools = await loadToolsRequest.fetch(() =>
        fetchAdminUserMcpTools(userId),
      );
      setTools(mcpTools ?? []);
    },
    [loadToolsRequest],
  );

  useEffect(() => {
    setAuthToken('');
    setPatToken('');
    setCopied(false);
    setValidationError(null);
    setRolesError(null);

    void loadUserRequest
      .fetch(async () => {
        const data = await fetchAdminUser(id);

        try {
          setRoles(await fetchAdminRoles());
        } catch (err) {
          setRoles([]);
          const code = extractExceptionCode(err);
          setRolesError(code ? t(`exceptions.${code}`) : null);
        }

        try {
          setToolPresets(await fetchToolPresets());
        } catch {
          setToolPresets([]);
        }

        try {
          setContentfulTags(await fetchContentfulTags());
        } catch {
          setContentfulTags([]);
        }

        await loadBuiltinTools();

        if (data.contentfulPatToken) {
          await loadTools(id);
        } else {
          setTools([]);
        }

        return data;
      })
      .then((data) => {
        if (!data) {
          setUser(null);
          return;
        }
        setUser(data);
      });
  }, [id, loadBuiltinTools, loadTools, loadUserRequest, t]);

  const connectedToolNames = useMemo(
    () => new Set(user?.tools.map((tool) => tool.name) ?? []),
    [user],
  );

  const connectedTagIds = useMemo(
    () =>
      new Set(
        user?.contentfulTags?.map((tag) => tag.contentfulTagId) ?? [],
      ),
    [user],
  );

  const roleOptions = useMemo(() => {
    const unique = new Set(roles);

    if (user) {
      unique.add(user.role);
    }

    return Array.from(unique);
  }, [roles, user]);

  const handleRegenerate = useCallback(async () => {
    setValidationError(null);
    setCopied(false);

    const result = await regenerateRequest.fetch(() =>
      regenerateAdminUserToken(id),
    );
    if (!result) {
      return;
    }
    setAuthToken(result.token);
  }, [id, regenerateRequest]);

  const handleCopy = useCallback(async () => {
    if (!authToken) {
      return;
    }

    try {
      await navigator.clipboard.writeText(authToken);
      setCopied(true);
    } catch {
      setValidationError(t('admin.users.errors.copyFailed'));
    }
  }, [authToken, t]);

  const handleSavePat = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      const trimmedToken = patToken.trim();

      if (!trimmedToken) {
        setValidationError(t('admin.users.errors.patRequired'));
        return;
      }

      setValidationError(null);

      const updatedUser = await savePatRequest.fetch(() =>
        setAdminUserContentfulPat(id, trimmedToken),
      );
      if (!updatedUser) {
        return;
      }
      setUser(updatedUser);
      setPatToken('');
      await loadTools(id);
    },
    [id, loadTools, patToken, savePatRequest, t],
  );

  const handleChangeRole = useCallback(
    async (role: AdminRole) => {
      if (!user || role === user.role || changeRoleRequest.isLoading) {
        return;
      }

      setValidationError(null);

      const updatedUser = await changeRoleRequest.fetch(() =>
        assignAdminUserRole(id, role),
      );
      if (!updatedUser) {
        return;
      }
      setUser(updatedUser);
    },
    [changeRoleRequest, id, user],
  );

  const handleToggleTool = useCallback(
    async (name: string) => {
      if (!user || pendingToolName || pendingPresetId || pendingTagId) {
        return;
      }

      const isConnected = connectedToolNames.has(name);

      setPendingToolName(name);
      setValidationError(null);

      const updatedUser = await toggleToolRequest.fetch(() =>
        isConnected
          ? disconnectAdminUserTool(id, name)
          : connectAdminUserTool(id, name),
      );
      setPendingToolName(null);
      if (!updatedUser) {
        return;
      }
      setUser(updatedUser);
    },
    [
      connectedToolNames,
      id,
      pendingPresetId,
      pendingTagId,
      pendingToolName,
      toggleToolRequest,
      user,
    ],
  );

  const handleApplyPreset = useCallback(
    async (presetId: string) => {
      if (!user || pendingToolName || pendingPresetId || pendingTagId) {
        return;
      }

      setPendingPresetId(presetId);
      setValidationError(null);

      const updatedUser = await applyPresetRequest.fetch(() =>
        applyAdminUserToolPreset(id, presetId),
      );
      setPendingPresetId(null);
      if (!updatedUser) {
        return;
      }
      setUser(updatedUser);
    },
    [
      applyPresetRequest,
      id,
      pendingPresetId,
      pendingTagId,
      pendingToolName,
      user,
    ],
  );

  const handleToggleTag = useCallback(
    async (contentfulTagId: string) => {
      if (!user || pendingToolName || pendingPresetId || pendingTagId) {
        return;
      }

      const isConnected = connectedTagIds.has(contentfulTagId);

      setPendingTagId(contentfulTagId);
      setValidationError(null);

      const updatedUser = await toggleTagRequest.fetch(() =>
        isConnected
          ? disconnectAdminUserContentfulTag(id, contentfulTagId)
          : connectAdminUserContentfulTag(id, contentfulTagId),
      );
      setPendingTagId(null);
      if (!updatedUser) {
        return;
      }
      setUser(updatedUser);
    },
    [
      connectedTagIds,
      id,
      pendingPresetId,
      pendingTagId,
      pendingToolName,
      toggleTagRequest,
      user,
    ],
  );

  const handleDelete = useCallback(async () => {
    setValidationError(null);

    const result = await deleteRequest.fetch(async () => {
      await deleteAdminUser(id);
    });
    if (result === null) {
      setIsDeleteDialogOpen(false);
      return;
    }
    navigate('/admin/users');
  }, [deleteRequest, id, navigate]);

  if (loadUserRequest.isLoading) {
    return (
      <Box sx={{ p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!user) {
    return (
      <Box sx={{ p: 2 }}>
        {validationError ??
        rolesError ??
        (deleteRequest.exception ??
          toggleTagRequest.exception ??
          applyPresetRequest.exception ??
          toggleToolRequest.exception ??
          changeRoleRequest.exception ??
          savePatRequest.exception ??
          regenerateRequest.exception ??
          loadUserRequest.exception) ? (
          <Typography variant="body2" color="error">
            {validationError ??
              rolesError ??
              t(
                `exceptions.${
                  deleteRequest.exception ??
                  toggleTagRequest.exception ??
                  applyPresetRequest.exception ??
                  toggleToolRequest.exception ??
                  changeRoleRequest.exception ??
                  savePatRequest.exception ??
                  regenerateRequest.exception ??
                  loadUserRequest.exception
                }`,
              )}
          </Typography>
        ) : null}
      </Box>
    );
  }

  const isBusy = Boolean(pendingToolName || pendingPresetId || pendingTagId);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        p: 2,
      }}
    >
      <Box>
        <Typography variant="h6">{user.email}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t(`admin.users.role.${user.role}`)}
        </Typography>
      </Box>
      {validationError ??
      rolesError ??
      (deleteRequest.exception ??
        toggleTagRequest.exception ??
        applyPresetRequest.exception ??
        toggleToolRequest.exception ??
        changeRoleRequest.exception ??
        savePatRequest.exception ??
        regenerateRequest.exception ??
        loadUserRequest.exception) ? (
        <Typography variant="body2" color="error">
          {validationError ??
            rolesError ??
            t(
              `exceptions.${
                deleteRequest.exception ??
                toggleTagRequest.exception ??
                applyPresetRequest.exception ??
                toggleToolRequest.exception ??
                changeRoleRequest.exception ??
                savePatRequest.exception ??
                regenerateRequest.exception ??
                loadUserRequest.exception
              }`,
            )}
        </Typography>
      ) : null}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        <Typography variant="subtitle1">
          {t('admin.users.authToken.label')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('admin.users.authToken.hint')}
        </Typography>
        <TextField
          value={authToken}
          placeholder={t('admin.users.authToken.empty')}
          slotProps={{
            input: {
              readOnly: true,
              endAdornment: (
                <Tooltip
                  title={
                    copied
                      ? t('admin.users.authToken.copied')
                      : t('admin.users.authToken.copy')
                  }
                >
                  <span>
                    <IconButton
                      aria-label={t('admin.users.authToken.copy')}
                      onClick={() => {
                        void handleCopy();
                      }}
                      disabled={!authToken}
                    >
                      <ContentCopyOutlinedIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              ),
            },
          }}
          fullWidth
        />
        <Button
          variant="contained"
          onClick={() => {
            void handleRegenerate();
          }}
          disabled={regenerateRequest.isLoading}
          sx={{ alignSelf: 'flex-start' }}
        >
          {t('admin.users.authToken.generate')}
        </Button>
      </Box>
      <Box
        component="form"
        onSubmit={handleSavePat}
        autoComplete="off"
        sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}
      >
        <Typography variant="subtitle1">{t('admin.users.pat.label')}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t('admin.users.pat.hint')}
        </Typography>
        <TextField
          type="password"
          value={patToken}
          onChange={(event) => setPatToken(event.target.value)}
          placeholder={
            user.contentfulPatToken
              ? '********'
              : t('admin.users.pat.placeholder')
          }
          slotProps={{
            htmlInput: {
              autoComplete: 'new-password',
              autoCorrect: 'off',
              autoCapitalize: 'off',
              spellCheck: false,
            },
          }}
          disabled={savePatRequest.isLoading}
          autoComplete="new-password"
          fullWidth
        />
        <Button
          type="submit"
          variant="contained"
          disabled={savePatRequest.isLoading}
          sx={{ alignSelf: 'flex-start' }}
        >
          {t('admin.users.pat.save')}
        </Button>
      </Box>
      <FormControl fullWidth disabled={changeRoleRequest.isLoading}>
        <InputLabel id="admin-user-role-label">
          {t('admin.users.role.label')}
        </InputLabel>
        <Select
          labelId="admin-user-role-label"
          label={t('admin.users.role.label')}
          value={user.role}
          onChange={(event) => {
            void handleChangeRole(event.target.value as AdminRole);
          }}
        >
          {roleOptions.map((role) => (
            <MenuItem key={role} value={role}>
              {t(`admin.users.role.${role}`)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        <Typography variant="subtitle1">
          {t('admin.users.tools.title')}
        </Typography>
        <ToolGroupComponent
          title={t('admin.users.tools.builtin')}
          tools={builtinTools}
          emptyLabel={t('admin.users.tools.empty')}
          selectedTools={connectedToolNames}
          disabled={isBusy}
          pendingName={pendingToolName}
          message={
            loadBuiltinToolsRequest.exception
              ? t(`exceptions.${loadBuiltinToolsRequest.exception}`)
              : null
          }
          onToggle={(name) => {
            void handleToggleTool(name);
          }}
        />
        <ToolGroupComponent
          title={t('admin.users.tools.mcp')}
          tools={tools}
          emptyLabel={t('admin.users.tools.empty')}
          selectedTools={connectedToolNames}
          disabled={isBusy}
          pendingName={pendingToolName}
          message={
            !user.contentfulPatToken
              ? t('admin.users.tools.patRequired')
              : loadToolsRequest.exception
                ? t(`exceptions.${loadToolsRequest.exception}`)
                : null
          }
          onToggle={(name) => {
            void handleToggleTool(name);
          }}
        />
        <Typography variant="subtitle1" sx={{ mt: 1 }}>
          {t('admin.users.toolPresets.title')}
        </Typography>
        {toolPresets.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('admin.users.toolPresets.empty')}
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {toolPresets.map((preset) => (
              <Box
                key={preset.id}
                sx={{ opacity: pendingPresetId === preset.id ? 0.6 : 1 }}
              >
                <ChatListItemComponent
                  chat={{
                    id: preset.id,
                    name: preset.name,
                    userId: '',
                    presetId: null,
                    createdAt: preset.createdAt,
                    updatedAt: preset.updatedAt,
                  }}
                  selected={false}
                  onClick={() => {
                    void handleApplyPreset(preset.id);
                  }}
                />
              </Box>
            ))}
          </Box>
        )}
        <Typography variant="subtitle1" sx={{ mt: 1 }}>
          {t('admin.users.contentfulTags.title')}
        </Typography>
        {contentfulTags.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('admin.users.contentfulTags.empty')}
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {contentfulTags.map((tag) => {
              const isConnected = connectedTagIds.has(tag.contentfulTagId);
              const isPending = pendingTagId === tag.contentfulTagId;

              return (
                <Chip
                  key={tag.id}
                  label={tag.contentfulTagId}
                  onClick={() => {
                    void handleToggleTag(tag.contentfulTagId);
                  }}
                  disabled={isBusy}
                  color={isConnected ? 'success' : 'default'}
                  variant={isConnected ? 'filled' : 'outlined'}
                  sx={{ opacity: isPending ? 0.6 : 1 }}
                />
              );
            })}
          </Box>
        )}
      </Box>
      <Box
        sx={{
          mt: 'auto',
          pt: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 1,
        }}
      >
        <Button
          color="error"
          variant="contained"
          onClick={() => setIsDeleteDialogOpen(true)}
        >
          {t('admin.users.delete.button')}
        </Button>
      </Box>
      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => {
          if (!deleteRequest.isLoading) {
            setIsDeleteDialogOpen(false);
          }
        }}
      >
        <DialogTitle>{t('admin.users.delete.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('admin.users.delete.confirm', { email: user.email })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsDeleteDialogOpen(false)}
            disabled={deleteRequest.isLoading}
          >
            {t('admin.users.delete.cancel')}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              void handleDelete();
            }}
            disabled={deleteRequest.isLoading}
          >
            {t('admin.users.delete.button')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
